import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage, useIntl} from 'react-intl';
import {userSelectors} from 'features/userSlice';
import {favoriteListsActions, favoriteListsSelectors} from 'features/favoriteListsSlice';
import {favoriteListErrorMessageId} from 'helper/favoriteListErrors';
import {useValidFavoriteListWineCount} from 'helper/useValidFavoriteListWineCount';
import Modal from './Modal';
import {ReactComponent as IconClose} from 'components/icons/close.svg';
import {ReactComponent as HeartIcon} from 'components/icons/heart.svg';
import {ReactComponent as HeartFullIcon} from 'components/icons/heart-full.svg';
import './FavoriteListsPopover.scss';

const FavoriteListsPopover = ({
  wineId,
  wineName,
  open,
  setOpen,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const soldTo = useSelector(userSelectors.soldTo);
  const favoriteLists = useSelector(favoriteListsSelectors.favoriteLists);
  const currentMembership = useSelector(favoriteListsSelectors.wineListMembership(wineId));
  const validListWineCount = useValidFavoriteListWineCount();

  const [checkedIds, setCheckedIds] = useState(new Set());
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [errorId, setErrorId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCheckedIds(new Set(currentMembership));
      setCreatingList(false);
      setNewListName('');
      setErrorId(null);
      setSubmitting(false);
    }
  }, [open]);

  const toggleList = (favoriteListId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(favoriteListId)) {
        next.delete(favoriteListId);
      } else {
        next.add(favoriteListId);
      }
      return next;
    });
  };

  const handleCreateList = async () => {
    const trimmed = newListName.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setErrorId(null);
    try {
      const created = await dispatch(favoriteListsActions.createFavoriteList({soldTo, name: trimmed})).unwrap();
      await dispatch(favoriteListsActions.addItemToList({soldTo, favoriteListId: created.id, wineId})).unwrap();
      setCheckedIds(prev => new Set(prev).add(created.id));
      setCreatingList(false);
      setNewListName('');
    } catch (err) {
      setErrorId(favoriteListErrorMessageId(err?.status));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrorId(null);
    try {
      const result = await dispatch(favoriteListsActions.setWineMembership({
        soldTo, wineId, favoriteListIds: Array.from(checkedIds),
      })).unwrap();
      if (result.blocked && result.blocked.length > 0) {
        setErrorId('FavoriteLists.Error.Blocked');
        setSubmitting(false);
        return;
      }
      setOpen(false);
    } catch (err) {
      setErrorId(favoriteListErrorMessageId(err?.status));
      setSubmitting(false);
    }
  };

  const handleRemoveFromAll = async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrorId(null);
    try {
      const result = await dispatch(favoriteListsActions.removeWineFromAllLists({soldTo, wineId})).unwrap();
      if (result.blocked) {
        setErrorId('FavoriteLists.Error.Blocked');
        setSubmitting(false);
        return;
      }
      setOpen(false);
    } catch (err) {
      setErrorId(favoriteListErrorMessageId(err?.status));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      name="favorite-lists-popover"
      open={open}
      setOpen={setOpen}
      position="middle-side"
      size="tiny"
      usePortal
      title={
        <div className="favorite-lists-popover-head">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><FormattedMessage id="FavoriteListsPopover.Title" /></h5>
            <IconClose className="cursor-pointer" onClick={() => setOpen(false)} />
          </div>
          {wineName && <div className="text-muted wine-name">{wineName}</div>}
        </div>
      }
      body={
        <div className="favorite-lists-popover-body">
          {favoriteLists && favoriteLists.map(list => (
            <label key={list.id} className="favorite-list-row d-flex align-items-center justify-content-between">
              <span className="d-flex align-items-center">
                {checkedIds.has(list.id) ?
                  <HeartFullIcon className="list-heart list-heart-full" />
                  :
                  <HeartIcon className="list-heart" />
                }
                <span>
                  <div className="name">{list.name}</div>
                  <div className="text-muted small">
                    <FormattedMessage id="FavoriteListsPopover.WineCount" values={{count: validListWineCount(list)}} />
                  </div>
                </span>
              </span>
              <input
                type="checkbox"
                className="favorite-list-checkbox"
                checked={checkedIds.has(list.id)}
                disabled={submitting}
                onChange={() => toggleList(list.id)}
              />
            </label>
          ))}

          {creatingList ?
            <div className="new-list-row d-flex align-items-center">
              <input
                type="text"
                className="form-control"
                autoFocus
                placeholder={intl.formatMessage({id: 'FavoriteListsPopover.NewListPlaceholder'})}
                value={newListName}
                disabled={submitting}
                onChange={ev => setNewListName(ev.target.value)}
                onKeyDown={ev => ev.key === 'Enter' && handleCreateList()}
              />
              <button
                type="button"
                className="btn create-btn"
                disabled={submitting || newListName.trim() === ''}
                onClick={handleCreateList}
              >
                <FormattedMessage id="FavoriteListsPopover.Create" />
              </button>
              <span className="cursor-pointer" onClick={() => {
                setCreatingList(false);
                setNewListName('');
              }}>
                <IconClose />
              </span>
            </div>
            :
            <div className="new-list-action d-flex align-items-center cursor-pointer" onClick={() => setCreatingList(true)}>
              <span className="new-list-icon">+</span>
              <FormattedMessage id="FavoriteListsPopover.NewListAction" />
            </div>
          }

          {errorId && <div className="text-danger small mt-2"><FormattedMessage id={errorId} /></div>}
        </div>
      }
      foot={
        <>
          <button type="button" className="primary full" disabled={submitting} onClick={handleDone}>
            <FormattedMessage id="FavoriteListsPopover.Done" />
          </button>
          {currentMembership.length > 0 &&
            <button type="button" className="full" disabled={submitting} onClick={handleRemoveFromAll}>
              <FormattedMessage id="FavoriteListsPopover.RemoveFromAll" />
            </button>
          }
        </>
      }
    />
  );
};

FavoriteListsPopover.propTypes = {
  wineId: PropTypes.string,
  wineName: PropTypes.string,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
};

export default FavoriteListsPopover;
